/* Selector con buscador para los campos que viajan por id.
 *
 * Lo usan Punto de Servicio, Superior Directo Nuevo (colaborador) y Cargo
 * Nuevo. Antes cada uno era un <IonSelect> con un <IonSearchbar> debajo, y eso
 * no se entendia: al escribir no cambiaba nada en pantalla, porque el
 * resultado caia dentro del select colapsado y habia que abrirlo despues. Con
 * el colaborador era peor, porque el select arrancaba vacio: quien lo abria
 * primero veia una hoja sin nada.
 *
 * Ahora es el mismo patron que el buscador de colaborador de la cabecera, en
 * esta misma pantalla: se escribe, los resultados aparecen como lista, se toca
 * uno y queda elegido con un boton para cambiarlo.
 *
 * No lista nada hasta que se escribe, y borrar el texto vacia la lista: una
 * lista que aparece sola invita a elegir de lo primero que se ve en vez de
 * buscar, y con cien cargos o con los puntos del fiscal eso casi nunca es lo
 * que se busca.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  IonButton,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSearchbar,
  IonText,
} from "@ionic/react";

export interface OpcionBuscada {
  id: string;
  titulo: string;
  detalle?: string;
}

interface Props {
  opciones: OpcionBuscada[];
  /** Id elegido, o "" si todavia no se eligio nada. */
  valor: string;
  onElegir: (id: string) => void;
  placeholder: string;
  /**
   * Busqueda remota. Sin esto la lista se filtra en el navegador, que es lo
   * que corresponde cuando ya estan todas las opciones (los cargos).
   */
  onBuscar?: (termino: string) => void;
  /** Minimo de letras para que la busqueda remota valga la pena. */
  minimo?: number;
  /** Aviso cuando no hay nada que ofrecer (el DWH no respondio, por ejemplo). */
  vacio?: string;
}

/** Tope de lo que se lista de una: mas que esto no se lee, se busca. */
const MAX_VISIBLES = 20;

/** Cuanto se espera antes de ir al servidor, para no pedir por cada tecla. */
const ESPERA_BUSQUEDA = 400;

const SolicitudRRHHBuscador: React.FC<Props> = ({
  opciones,
  valor,
  onElegir,
  placeholder,
  onBuscar,
  minimo = 2,
  vacio,
}) => {
  const [termino, setTermino] = useState("");
  const temporizador = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (temporizador.current) window.clearTimeout(temporizador.current);
    },
    [],
  );

  /**
   * El texto se guarda en cada tecla y el freno queda SOLO para la consulta al
   * servidor.
   *
   * Con el `debounce` del IonSearchbar no se podia borrar: el value es
   * controlado, asi que React volvia a pintar el texto viejo antes de que el
   * ionChange frenado llegara, y la "x" no hacia nada. Ese era el "si se borra
   * no limpia" que se reporto.
   */
  const alEscribir = (texto: string) => {
    setTermino(texto);
    if (!onBuscar) return;
    if (temporizador.current) window.clearTimeout(temporizador.current);
    if (texto.trim().length < minimo) return;
    temporizador.current = window.setTimeout(
      () => onBuscar(texto),
      ESPERA_BUSQUEDA,
    );
  };

  const elegida = opciones.find((o) => o.id === valor);
  if (valor && elegida) {
    return (
      <IonItem lines="none">
        <IonLabel>
          <h3>{elegida.titulo}</h3>
          {elegida.detalle && <p>{elegida.detalle}</p>}
        </IonLabel>
        <IonButton
          fill="clear"
          size="small"
          slot="end"
          onClick={() => {
            setTermino("");
            onElegir("");
          }}
        >
          Cambiar
        </IonButton>
      </IonItem>
    );
  }

  const termnorm = termino.trim().toLowerCase();
  // Con busqueda remota se espera a tener suficientes letras; con lista local
  // (los cargos) alcanza con una.
  const minimoEfectivo = onBuscar ? minimo : 1;
  const hayTermino = termnorm.length >= minimoEfectivo;

  // Nada se lista hasta que se escribe, y borrar el texto vacia la lista: una
  // lista que aparece sola invita a elegir de lo primero que se ve en vez de
  // buscar, y con cien cargos o los puntos del fiscal eso casi nunca es lo que
  // se busca. El filtro es SIEMPRE por lo tipeado, tambien cuando las
  // opciones vienen de una busqueda remota: el servidor trae candidatos y esto
  // muestra solo los que coinciden con lo que se escribio.
  const filtradas = !hayTermino
    ? []
    : opciones.filter(
        (o) =>
          o.titulo.toLowerCase().includes(termnorm) ||
          (o.detalle || "").toLowerCase().includes(termnorm),
      );
  const visibles = filtradas.slice(0, MAX_VISIBLES);

  return (
    <>
      <IonSearchbar
        value={termino}
        placeholder={placeholder}
        onIonInput={(e) =>
          alEscribir((e.target as HTMLIonSearchbarElement).value || "")
        }
        onIonClear={() => alEscribir("")}
        style={{ paddingInline: 0 }}
      />

      {!opciones.length && vacio && (
        <IonNote color="danger" style={{ paddingInline: "1rem" }}>
          {vacio}
        </IonNote>
      )}

      {!hayTermino && !(vacio && !opciones.length) && (
        <IonText color="medium">
          <p style={{ margin: "0 0 0 1rem" }}>
            {minimoEfectivo > 1
              ? `Escribí al menos ${minimoEfectivo} letras para buscar.`
              : "Escribí para buscar."}
          </p>
        </IonText>
      )}

      {hayTermino && !visibles.length && (
        <IonText color="medium">
          <p style={{ margin: "0 0 0 1rem" }}>Sin resultados.</p>
        </IonText>
      )}

      <IonList>
        {visibles.map((o) => (
          <IonItem key={o.id} button onClick={() => onElegir(o.id)}>
            <IonLabel>
              <h3>{o.titulo}</h3>
              {o.detalle && <p>{o.detalle}</p>}
            </IonLabel>
          </IonItem>
        ))}
      </IonList>

      {filtradas.length > MAX_VISIBLES && (
        <IonText color="medium">
          <p style={{ margin: "0 0 0 1rem" }}>
            Se muestran {MAX_VISIBLES} de {filtradas.length}. Afiná la búsqueda.
          </p>
        </IonText>
      )}
    </>
  );
};

export default SolicitudRRHHBuscador;
